/**
 * Usuários e permissões (`/app/usuarios`) — uma tela, duas abas.
 *
 * "Usuários" é listagem, como `TelaClientes.tsx` — mesma forma de tabela,
 * mesmo padrão de gaveta para "convidar", mesmo botão de editar por linha.
 * "Papéis e permissões" mostra os seis papéis de fábrica (somente leitura,
 * "definido pela Lumma Software") e os papéis PERSONALIZADOS da loja —
 * criados, editados e excluídos aqui, com a matriz permissão × nível que já
 * decide toda requisição (`GET /perfis`, espelho de `porteiro/papel.go` mais
 * a loja).
 *
 * As duas telas viviam em rotas separadas (`/app/usuarios` e `/app/perfis`);
 * viraram abas de uma tela só, com `Segmentado` — a rota antiga continua
 * funcionando (ver `TelaPerfis.tsx`, agora um redirecionamento).
 *
 * Guardado por `users.manage` no servidor (só o Administrador, ou quem tiver
 * essa permissão via papel personalizado, tem nível total nessa permissão) —
 * o front só esconde a rota do menu; quem decide é o porteiro
 * (CONSTITUICAO.md §4.1). Chegar aqui sem a permissão mostra a falha que a
 * API devolve, e não finge que a tela existe.
 */
import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Botao } from '../../componentes/Botao'
import { Dialogo, RodapeDoDialogo } from '../../componentes/Dialogo'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone } from '../../componentes/Icone'
import { Segmentado } from '../../componentes/Segmentado'
import { Selo, type TomSelo } from '../../componentes/Selo'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import {
  carregarMatrizDePermissoes,
  criarPapelPersonalizado,
  atualizarPapelPersonalizado,
  excluirPapelPersonalizado,
  type MatrizDePermissoes,
  type NivelPermissao,
  type PapelDaMatriz,
  type PermissaoDaMatriz,
} from '../../plataforma/perfis'
import {
  PAPEIS_CONVITE,
  atualizarUsuario,
  carregarUsuarios,
  convidarUsuario,
  type LinhaUsuario,
  type ListaDeUsuarios,
  type PapelCompleto,
} from '../../plataforma/usuarios'
import { LayoutApp } from '../app/LayoutApp'

type Aba = 'usuarios' | 'perfis'

/** "12/03/2026" ou travessão — a mesma regra de "Meu perfil". */
function dataBR(iso: string | undefined): string {
  if (!iso) return '—'
  const quando = new Date(iso)
  if (Number.isNaN(quando.getTime())) return '—'
  return quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function TelaUsuarios() {
  const [parametros, setParametros] = useSearchParams()
  const aba: Aba = parametros.get('aba') === 'perfis' ? 'perfis' : 'usuarios'

  const [gavetaAberta, setGavetaAberta] = useState(false)

  function trocarAba(proxima: Aba) {
    setParametros(proxima === 'usuarios' ? {} : { aba: proxima }, { replace: true })
  }

  return (
    <LayoutApp
      titulo="Usuários e permissões"
      subtitulo="Quem entra no sistema, com qual papel e comissão."
      trilha={[{ rotulo: 'Configurações', para: '/app/configuracoes' }, { rotulo: 'Usuários e permissões' }]}
      voltarPara="/app/configuracoes"
      acoes={
        aba === 'usuarios' ? (
          <Botao icone="person_add" onClick={() => setGavetaAberta(true)}>
            Convidar usuário
          </Botao>
        ) : undefined
      }
    >
      <div className="flex w-full flex-col gap-4">
        <Segmentado
          papel="aba"
          rotuloDoGrupo="Usuários ou papéis e permissões"
          opcoes={[
            { chave: 'usuarios' as Aba, rotulo: 'Usuários' },
            { chave: 'perfis' as Aba, rotulo: 'Papéis e permissões' },
          ]}
          valor={aba}
          aoTrocar={trocarAba}
        />

        {aba === 'usuarios' ? (
          <PainelUsuarios gavetaAberta={gavetaAberta} aoFecharGaveta={() => setGavetaAberta(false)} />
        ) : (
          <PainelPerfis />
        )}
      </div>
    </LayoutApp>
  )
}

/* ================================================================ usuários */

function PainelUsuarios({
  gavetaAberta,
  aoFecharGaveta,
}: {
  gavetaAberta: boolean
  aoFecharGaveta: () => void
}) {
  const [lista, setLista] = useState<ListaDeUsuarios | null>(null)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)
  const [busca, setBusca] = useState('')
  const [somenteAtivos, setSomenteAtivos] = useState(false)

  const [editando, setEditando] = useState<LinhaUsuario | null>(null)

  useEffect(() => {
    const controle = new AbortController()
    carregarUsuarios(controle.signal)
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os usuários.')
      })
    return () => controle.abort()
  }, [versao])

  const filtrados = useMemo(() => {
    if (!lista) return []
    const termo = busca.trim().toLowerCase()
    return lista.usuarios.filter((u) => {
      if (somenteAtivos && u.status !== 'ativo') return false
      if (!termo) return true
      return u.nome.toLowerCase().includes(termo) || u.email.toLowerCase().includes(termo)
    })
  }, [lista, busca, somenteAtivos])

  const totalAtivos = lista ? lista.usuarios.filter((u) => u.status === 'ativo').length : 0

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Segmentado
          rotuloDoGrupo="Filtrar por situação"
          opcoes={[
            { chave: 'todos', rotulo: 'Todos', contagem: lista?.total ?? 0 },
            { chave: 'ativos', rotulo: 'Ativos', contagem: totalAtivos },
          ]}
          valor={somenteAtivos ? 'ativos' : 'todos'}
          aoTrocar={(chave) => setSomenteAtivos(chave === 'ativos')}
        />

        <div className="relative min-w-[220px] flex-1">
          <Icone
            nome="search"
            tamanho={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-texto-fraco"
          />
          <input
            type="search"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar usuário por nome ou e-mail"
            className="campo pl-9"
          />
        </div>
      </div>

      {erro && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
          <span className="min-w-0 flex-1">{erro}</span>
          <button
            type="button"
            onClick={() => setVersao((n) => n + 1)}
            className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {!lista ? (
        <div className="h-[280px] w-full animate-pulse rounded-sistema bg-superficie" />
      ) : filtrados.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 rounded-sistema border border-borda bg-superficie px-4 py-14 text-center">
          <Icone nome="group" tamanho={26} className="text-inativo" />
          <p className="text-sm font-semibold text-tinta">
            {lista.usuarios.length === 0 ? 'Nenhum usuário ainda' : 'Nenhum usuário encontrado'}
          </p>
          <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
            {lista.usuarios.length === 0
              ? 'Convide alguém da equipe para entrar no sistema.'
              : 'Ajuste a busca ou o filtro de situação.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] border-collapse">
                <caption className="sr-only">Usuários e convites da loja</caption>
                <thead>
                  <tr className="bg-fundo">
                    {['Usuário', 'Papel', 'Comissão', 'Última atividade', 'Situação', ''].map((titulo, i) => (
                      <th
                        key={titulo || 'acoes'}
                        scope="col"
                        className={`border-b border-borda py-2.5 text-left text-[12.5px] font-semibold text-texto-suave ${
                          i === 0 ? 'pr-3 pl-4' : 'px-3'
                        }`}
                      >
                        {titulo}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((linha, i) => (
                    <LinhaDaTabela
                      key={linha.email}
                      linha={linha}
                      ultima={i === filtrados.length - 1}
                      aoEditar={() => setEditando(linha)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[12.5px] text-texto-suave">
            {filtrados.length} de {lista.total} {lista.total === 1 ? 'usuário' : 'usuários'}
          </p>
        </>
      )}

      {gavetaAberta && (
        <GavetaConvidar
          aoFechar={aoFecharGaveta}
          aoConcluir={() => {
            aoFecharGaveta()
            setVersao((n) => n + 1)
          }}
        />
      )}

      {editando && (
        <DialogoEditarAcesso
          usuario={editando}
          aoFechar={() => setEditando(null)}
          aoConcluir={(aviso) => {
            setEditando(null)
            setVersao((n) => n + 1)
            mostrarAviso(aviso)
          }}
        />
      )}
    </>
  )
}

/** Comissão em pontos-base → percentual com uma casa: 1250 → "12,5%". */
function comissaoFormatada(pontos: number): string {
  if (!pontos) return '—'
  return (pontos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + '%'
}

function LinhaDaTabela({
  linha,
  ultima,
  aoEditar,
}: {
  linha: LinhaUsuario
  ultima: boolean
  aoEditar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'
  const ehConvite = linha.status === 'convidado'

  return (
    <tr>
      <td className={`py-2.5 pr-3 pl-4 ${borda}`}>
        <div className="text-[13.5px] font-semibold text-tinta">
          {linha.nome || <span className="font-normal text-texto-fraco">—</span>}
        </div>
        <div className="text-[12.5px] text-texto-suave">{linha.email}</div>
      </td>
      <td className={`px-3 py-2.5 text-[13px] text-texto ${borda}`}>
        <Selo tom={linha.papel_personalizado_id ? 'roxo' : 'neutro'}>{linha.papel_rotulo}</Selo>
      </td>
      <td className={`tabular px-3 py-2.5 text-[13px] text-texto ${borda}`}>
        {comissaoFormatada(linha.comissao_pontos)}
      </td>
      <td className={`tabular px-3 py-2.5 text-[12.5px] text-texto-suave ${borda}`}>
        {ehConvite ? 'Convidado em ' + dataBR(linha.criado_em) : dataBR(linha.ultimo_acesso_em) === '—' ? 'Nunca acessou' : dataBR(linha.ultimo_acesso_em)}
      </td>
      <td className={`px-3 py-2.5 ${borda}`}>
        <SituacaoDoUsuario status={linha.status} rotulo={linha.status_rotulo} />
      </td>
      <td className={`px-3 py-2.5 text-right ${borda}`}>
        {/* Convite pendente não tem ação por aqui: trocar papel ou "ativar"
            um convite não é o que existe hoje — a pessoa aceita pelo e-mail,
            e SÓ ENTÃO vira uma linha editável. Ensinar um botão que recusaria
            é pior que não ter o botão. */}
        {!ehConvite && (
          <button
            type="button"
            onClick={aoEditar}
            className="inline-flex h-8 items-center gap-1.5 rounded-sistema border border-borda bg-superficie px-3 text-[12.5px] font-semibold text-texto hover:bg-divisoria"
          >
            <Icone nome="edit_note" tamanho={15} />
            Editar acesso
          </button>
        )}
      </td>
    </tr>
  )
}

function SituacaoDoUsuario({ status, rotulo }: { status: LinhaUsuario['status']; rotulo: string }) {
  const estilo =
    status === 'ativo'
      ? 'border-ok-suave bg-ok-suave text-ok-texto'
      : status === 'bloqueado'
        ? 'border-erro-suave bg-erro-suave text-erro-texto'
        : 'border-atencao-suave bg-atencao-suave text-atencao-texto'

  return (
    <span className={`inline-flex items-center rounded-[6px] border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${estilo}`}>
      {rotulo}
    </span>
  )
}

/* ---------------------------------------------------------------- convite */

function GavetaConvidar({
  aoFechar,
  aoConcluir,
}: {
  aoFechar: () => void
  aoConcluir: () => void
}) {
  const [email, setEmail] = useState('')
  const [papel, setPapel] = useState<PapelCompleto>('vendedor')
  const [papelPersonalizadoId, setPapelPersonalizadoId] = useState('')
  const [personalizados, setPersonalizados] = useState<PapelDaMatriz[]>([])
  const [erros, setErros] = useState<Record<string, string>>({})
  const [aviso, setAviso] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    const controle = new AbortController()
    carregarMatrizDePermissoes(controle.signal)
      .then((matriz) => setPersonalizados(matriz.papeis.filter((p) => p.personalizado)))
      .catch(() => {
        // A gaveta funciona sem o seletor opcional: convidar com só o papel
        // continua possível mesmo se a matriz não carregar.
      })
    return () => controle.abort()
  }, [])

  async function enviar() {
    setErros({})
    setAviso('')
    setEnviando(true)
    try {
      const resultado = await convidarUsuario({
        email,
        papel,
        papel_personalizado_id: papelPersonalizadoId || undefined,
      })
      mostrarAviso(`${resultado.mensagem} ${resultado.email} entra como ${resultado.papel_rotulo.toLowerCase()}.`)
      aoConcluir()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível enviar o convite.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Gaveta rotulo="Convidar usuário" largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta titulo="Convidar usuário" subtitulo="Envia um link de acesso por e-mail" aoFechar={aoFechar} />

      <CorpoDaGaveta>
        <form
          onSubmit={(evento) => {
            evento.preventDefault()
            void enviar()
          }}
          className="flex flex-col gap-4"
        >
          <p className="rounded-sistema bg-marca-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-marca-forte">
            O convidado recebe um e-mail e cria a própria senha — você não define senha por ninguém.
          </p>

          {aviso && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
              {aviso}
            </div>
          )}

          <div>
            <label htmlFor="convite-email" className="mb-1.5 block text-[13px] font-semibold text-texto">
              E-mail
            </label>
            <input
              id="convite-email"
              type="email"
              value={email}
              onChange={(evento) => setEmail(evento.target.value)}
              placeholder="pessoa@email.com"
              aria-invalid={erros.email ? true : undefined}
              className="campo"
              required
            />
            <p className="mt-[6px] text-[12.5px] text-texto-fraco">O convite e o login serão neste e-mail.</p>
            {erros.email && (
              <p role="alert" className="mt-[6px] flex items-center gap-1.5 text-[12.5px] font-medium text-erro">
                <Icone nome="error" tamanho={14} />
                {erros.email}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="convite-papel" className="mb-1.5 block text-[13px] font-semibold text-texto">
              Papel
            </label>
            <select
              id="convite-papel"
              value={papel}
              onChange={(evento) => setPapel(evento.target.value as PapelCompleto)}
              aria-invalid={erros.papel ? true : undefined}
              className="campo"
            >
              {PAPEIS_CONVITE.map((item) => (
                <option key={item.chave} value={item.chave}>
                  {item.rotulo}
                </option>
              ))}
            </select>
            {erros.papel && (
              <p role="alert" className="mt-[6px] flex items-center gap-1.5 text-[12.5px] font-medium text-erro">
                <Icone nome="error" tamanho={14} />
                {erros.papel}
              </p>
            )}
            <p className="mt-[6px] text-[12.5px] text-texto-fraco">
              Confira "Papéis e permissões" para o que cada função pode fazer.
            </p>
          </div>

          <div>
            <label htmlFor="convite-papel-personalizado" className="mb-1.5 block text-[13px] font-semibold text-texto">
              Perfil de permissões — opcional
            </label>
            <select
              id="convite-papel-personalizado"
              value={papelPersonalizadoId}
              onChange={(evento) => setPapelPersonalizadoId(evento.target.value)}
              aria-invalid={erros.papel_personalizado_id ? true : undefined}
              className="campo"
            >
              <option value="">Nenhum (usar padrão do papel)</option>
              {personalizados.map((item) => (
                <option key={item.chave} value={item.chave}>
                  {item.rotulo}
                </option>
              ))}
            </select>
            {erros.papel_personalizado_id && (
              <p role="alert" className="mt-[6px] flex items-center gap-1.5 text-[12.5px] font-medium text-erro">
                <Icone nome="error" tamanho={14} />
                {erros.papel_personalizado_id}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <Botao type="submit" icone="person_add" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar convite'}
            </Botao>
          </div>
        </form>
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

/* ----------------------------------------------------------- editar acesso */

function DialogoEditarAcesso({
  usuario,
  aoFechar,
  aoConcluir,
}: {
  usuario: LinhaUsuario
  aoFechar: () => void
  aoConcluir: (aviso: string) => void
}) {
  const [papel, setPapel] = useState<PapelCompleto>(usuario.papel)
  const [status, setStatus] = useState<'ativo' | 'bloqueado'>(
    usuario.status === 'bloqueado' ? 'bloqueado' : 'ativo',
  )
  const [papelPersonalizadoId, setPapelPersonalizadoId] = useState(usuario.papel_personalizado_id ?? '')
  const [personalizados, setPersonalizados] = useState<PapelDaMatriz[]>([])
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    const controle = new AbortController()
    carregarMatrizDePermissoes(controle.signal)
      .then((matriz) => setPersonalizados(matriz.papeis.filter((p) => p.personalizado)))
      .catch(() => {})
    return () => controle.abort()
  }, [])

  const vaiBloquear = status === 'bloqueado' && usuario.status === 'ativo'

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setErro('')
    setSalvando(true)
    try {
      await atualizarUsuario(usuario.email, { papel, status, papel_personalizado_id: papelPersonalizadoId })
      aoConcluir(`Acesso de ${usuario.nome || usuario.email} atualizado.`)
    } catch (causa: unknown) {
      // O 409 do invariante ("a loja precisa de ao menos um administrador
      // ativo") chega aqui como mensagem pronta — é o servidor que decide,
      // não a tela (CONSTITUICAO.md §4.1).
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível atualizar o acesso.')
      setSalvando(false)
    }
  }

  return (
    <Dialogo icone="group" titulo={`Editar acesso de ${usuario.nome || usuario.email}`} aoFechar={aoFechar}>
      <form onSubmit={(evento) => void enviar(evento)} noValidate>
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="editar-papel" className="mb-1.5 block text-[13px] font-semibold text-texto">
              Papel
            </label>
            <select
              id="editar-papel"
              value={papel}
              onChange={(evento) => setPapel(evento.target.value as PapelCompleto)}
              className="campo"
            >
              {PAPEIS_CONVITE.map((item) => (
                <option key={item.chave} value={item.chave}>
                  {item.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="editar-papel-personalizado" className="mb-1.5 block text-[13px] font-semibold text-texto">
              Perfil de permissões — opcional
            </label>
            <select
              id="editar-papel-personalizado"
              value={papelPersonalizadoId}
              onChange={(evento) => setPapelPersonalizadoId(evento.target.value)}
              className="campo"
            >
              <option value="">Nenhum (usar padrão do papel)</option>
              {personalizados.map((item) => (
                <option key={item.chave} value={item.chave}>
                  {item.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className="mb-1.5 block text-[13px] font-semibold text-texto">Situação</span>
            <Segmentado
              opcoes={[
                { chave: 'ativo', rotulo: 'Ativo' },
                { chave: 'bloqueado', rotulo: 'Bloqueado' },
              ]}
              valor={status}
              aoTrocar={setStatus}
              rotuloDoGrupo="Situação de acesso"
            />
            {vaiBloquear && (
              <p className="mt-1.5 text-[12.5px] text-texto-fraco">
                A pessoa perde o acesso no próximo clique — não precisa esperar a sessão expirar.
              </p>
            )}
          </div>
        </div>

        {erro && (
          <p role="alert" className="mt-3 flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] font-medium text-erro-texto">
            <Icone nome="error" tamanho={16} className="mt-px shrink-0" />
            <span>{erro}</span>
          </p>
        )}

        <RodapeDoDialogo
          confirmar="Salvar"
          confirmando="Salvando…"
          ocupado={salvando}
          destrutivo={vaiBloquear}
          aoCancelar={aoFechar}
        />
      </form>
    </Dialogo>
  )
}

/* ================================================================== perfis */

const ROTULO_NIVEL: Record<NivelPermissao, string> = {
  full: 'Total',
  pin: 'Com PIN',
  read: 'Só ver',
  none: 'Sem acesso',
}

const TOM_NIVEL: Record<NivelPermissao, TomSelo> = {
  full: 'ok',
  pin: 'atencao',
  read: 'info',
  none: 'neutro',
}

const NIVEIS_SELECIONAVEIS: NivelPermissao[] = ['none', 'read', 'pin', 'full']

const NOVO_PAPEL = '__novo__'

type Grupo = { nome: string; permissoes: PermissaoDaMatriz[] }

/** Agrupa preservando a ordem de chegada — a mesma ordem de `porteiro.Grupos`. */
function agruparPorGrupo(permissoes: PermissaoDaMatriz[]): Grupo[] {
  const grupos: Grupo[] = []
  for (const permissao of permissoes) {
    let grupo = grupos.find((g) => g.nome === permissao.grupo)
    if (!grupo) {
      grupo = { nome: permissao.grupo, permissoes: [] }
      grupos.push(grupo)
    }
    grupo.permissoes.push(permissao)
  }
  return grupos
}

function PainelPerfis() {
  const [matriz, setMatriz] = useState<MatrizDePermissoes | null>(null)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)
  const [selecionado, setSelecionado] = useState<string | null>(null)

  useEffect(() => {
    const controle = new AbortController()
    carregarMatrizDePermissoes(controle.signal)
      .then((dados) => {
        setMatriz(dados)
        setErro('')
        setSelecionado((atual) => atual ?? dados.papeis[0]?.chave ?? null)
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a matriz de permissões.')
      })
    return () => controle.abort()
  }, [versao])

  if (erro) {
    return (
      <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
        {erro}
      </div>
    )
  }

  if (!matriz) {
    return <div className="h-[420px] w-full animate-pulse rounded-sistema bg-superficie" />
  }

  const grupos = agruparPorGrupo(matriz.permissoes)
  const deFabrica = matriz.papeis.filter((p) => !p.personalizado)
  const personalizados = matriz.papeis.filter((p) => p.personalizado)
  const papelSelecionado =
    selecionado === NOVO_PAPEL ? null : matriz.papeis.find((p) => p.chave === selecionado) ?? null

  function recarregar(mensagem?: string) {
    setVersao((n) => n + 1)
    if (mensagem) mostrarAviso(mensagem)
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr] lg:items-start">
      <nav aria-label="Papéis" className="flex flex-col gap-4 rounded-sistema border border-borda bg-superficie p-3">
        <div>
          <h3 className="px-2 pb-1.5 text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase">
            Do sistema
          </h3>
          <ul className="flex flex-col gap-0.5">
            {deFabrica.map((papel) => (
              <ItemDaLista
                key={papel.chave}
                papel={papel}
                ativo={papel.chave === selecionado}
                aoSelecionar={() => setSelecionado(papel.chave)}
              />
            ))}
          </ul>
        </div>

        <div>
          <h3 className="px-2 pb-1.5 text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase">
            Personalizados
          </h3>
          <ul className="flex flex-col gap-0.5">
            {personalizados.map((papel) => (
              <ItemDaLista
                key={papel.chave}
                papel={papel}
                ativo={papel.chave === selecionado}
                aoSelecionar={() => setSelecionado(papel.chave)}
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setSelecionado(NOVO_PAPEL)}
            className={`mt-1 flex w-full items-center gap-1.5 rounded-sistema px-2 py-2 text-left text-[13px] font-semibold ${
              selecionado === NOVO_PAPEL ? 'bg-marca-suave text-marca-forte' : 'text-marca hover:bg-divisoria'
            }`}
          >
            <Icone nome="add" tamanho={16} />
            Novo
          </button>
        </div>
      </nav>

      {selecionado === NOVO_PAPEL ? (
        <FormularioPapelPersonalizado
          key="novo"
          grupos={grupos}
          aoSalvar={async (pedido) => {
            const criado = await criarPapelPersonalizado(pedido)
            setSelecionado(criado.id)
            recarregar('Papel personalizado criado.')
          }}
        />
      ) : papelSelecionado?.personalizado ? (
        <FormularioPapelPersonalizado
          key={papelSelecionado.chave}
          papel={papelSelecionado}
          grupos={grupos}
          aoSalvar={async (pedido) => {
            await atualizarPapelPersonalizado(papelSelecionado.chave, pedido)
            recarregar('Papel personalizado atualizado.')
          }}
          aoExcluir={async () => {
            await excluirPapelPersonalizado(papelSelecionado.chave)
            setSelecionado(null)
            recarregar('Papel personalizado excluído.')
          }}
        />
      ) : papelSelecionado ? (
        <DetalhePapelDeFabrica papel={papelSelecionado} grupos={grupos} />
      ) : (
        <div className="rounded-sistema border border-borda bg-superficie p-6 text-[13px] text-texto-suave">
          Selecione um papel à esquerda.
        </div>
      )}
    </div>
  )
}

function ItemDaLista({
  papel,
  ativo,
  aoSelecionar,
}: {
  papel: PapelDaMatriz
  ativo: boolean
  aoSelecionar: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={aoSelecionar}
        className={`flex w-full items-center justify-between gap-2 rounded-sistema px-2 py-2 text-left text-[13px] font-semibold ${
          ativo ? 'bg-marca-suave text-marca-forte' : 'text-texto hover:bg-divisoria'
        }`}
      >
        <span className="truncate">{papel.rotulo}</span>
        {papel.personalizado && papel.em_uso && (
          <span className="shrink-0 text-[11px] font-normal text-texto-fraco">em uso</span>
        )}
      </button>
    </li>
  )
}

function DetalhePapelDeFabrica({ papel, grupos }: { papel: PapelDaMatriz; grupos: Grupo[] }) {
  return (
    <div className="rounded-sistema border border-borda bg-superficie">
      <div className="border-b border-borda p-5">
        <h2 className="font-titulo text-[16px] font-bold text-titulo">{papel.rotulo}</h2>
        <p className="mt-1 text-[13px] text-texto-suave">{papel.descricao}</p>
        <p className="mt-2 text-[12.5px] text-texto-fraco italic">
          Definido pela Lumma Software — não se edita por aqui.
        </p>
      </div>
      <TabelaDeNiveisSomenteLeitura grupos={grupos} chaveDoPapel={papel.chave} />
    </div>
  )
}

function TabelaDeNiveisSomenteLeitura({ grupos, chaveDoPapel }: { grupos: Grupo[]; chaveDoPapel: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] border-collapse">
        <caption className="sr-only">Permissões do papel {chaveDoPapel}</caption>
        <tbody>
          {grupos.map((grupo) => (
            <Fragment key={grupo.nome}>
              <tr>
                <th
                  colSpan={2}
                  scope="colgroup"
                  className="border-b border-divisoria bg-fundo px-5 py-1.5 text-left text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase"
                >
                  {grupo.nome}
                </th>
              </tr>
              {grupo.permissoes.map((permissao, i) => {
                const nivel = permissao.niveis[chaveDoPapel] ?? 'none'
                return (
                  <tr
                    key={permissao.chave}
                    className={i === grupo.permissoes.length - 1 ? '' : 'border-b border-divisoria'}
                  >
                    <td className="py-2.5 pr-3 pl-5 text-[13px] text-tinta">{permissao.rotulo}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Selo tom={TOM_NIVEL[nivel]}>{ROTULO_NIVEL[nivel]}</Selo>
                    </td>
                  </tr>
                )
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FormularioPapelPersonalizado({
  papel,
  grupos,
  aoSalvar,
  aoExcluir,
}: {
  papel?: PapelDaMatriz
  grupos: Grupo[]
  aoSalvar: (pedido: { nome: string; descricao: string; niveis: Record<string, NivelPermissao> }) => Promise<void>
  aoExcluir?: () => Promise<void>
}) {
  const [nome, setNome] = useState(papel?.rotulo ?? '')
  const [descricao, setDescricao] = useState(papel?.descricao ?? '')
  const [niveis, setNiveis] = useState<Record<string, NivelPermissao>>(() => {
    const inicial: Record<string, NivelPermissao> = {}
    for (const grupo of grupos) {
      for (const permissao of grupo.permissoes) {
        inicial[permissao.chave] = papel ? (permissao.niveis[papel.chave] ?? 'none') : 'none'
      }
    }
    return inicial
  })
  const [erro, setErro] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState(false)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
  const [excluindo, setExcluindo] = useState(false)

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    setErro('')
    setErros({})
    setSalvando(true)
    try {
      await aoSalvar({ nome, descricao, niveis })
    } catch (causa: unknown) {
      if (causa instanceof ErroApi) {
        setErro(causa.message)
        setErros(causa.campos)
      } else {
        setErro('Não foi possível salvar o papel.')
      }
    } finally {
      setSalvando(false)
    }
  }

  async function excluir() {
    if (!aoExcluir) return
    if (!confirmandoExclusao) {
      setConfirmandoExclusao(true)
      return
    }
    setExcluindo(true)
    setErro('')
    try {
      await aoExcluir()
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível excluir o papel.')
      setExcluindo(false)
      setConfirmandoExclusao(false)
    }
  }

  return (
    <form onSubmit={(evento) => void salvar(evento)} noValidate className="flex flex-col rounded-sistema border border-borda bg-superficie">
      <div className="flex flex-col gap-4 border-b border-borda p-5">
        <div>
          <label htmlFor="papel-nome" className="mb-1.5 block text-[13px] font-semibold text-texto">
            Nome do papel
          </label>
          <input
            id="papel-nome"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            aria-invalid={erros.nome ? true : undefined}
            className="campo"
            required
          />
          {erros.nome && (
            <p role="alert" className="mt-[6px] flex items-center gap-1.5 text-[12.5px] font-medium text-erro">
              <Icone nome="error" tamanho={14} />
              {erros.nome}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="papel-descricao" className="mb-1.5 block text-[13px] font-semibold text-texto">
            Descrição — opcional
          </label>
          <textarea
            id="papel-descricao"
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            rows={2}
            className="campo"
          />
        </div>

        {aoExcluir && (
          <div className="flex items-center gap-2">
            <Botao
              type="button"
              variante="perigo"
              porte="padrao"
              disabled={papel?.em_uso || excluindo}
              onClick={() => void excluir()}
            >
              {excluindo ? 'Excluindo…' : confirmandoExclusao ? 'Confirmar exclusão' : 'Excluir papel'}
            </Botao>
            {confirmandoExclusao && !excluindo && (
              <button
                type="button"
                onClick={() => setConfirmandoExclusao(false)}
                className="text-[12.5px] font-semibold text-texto-suave hover:text-texto"
              >
                Cancelar exclusão
              </button>
            )}
            {papel?.em_uso && (
              <span className="text-[12.5px] text-texto-fraco">
                Ainda atribuído a alguém — troque o acesso dessas pessoas antes de excluir.
              </span>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <caption className="sr-only">Matriz de permissões do papel personalizado</caption>
          <tbody>
            {grupos.map((grupo) => (
              <Fragment key={grupo.nome}>
                <tr>
                  <th
                    colSpan={2}
                    scope="colgroup"
                    className="border-b border-divisoria bg-fundo px-5 py-1.5 text-left text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase"
                  >
                    {grupo.nome}
                  </th>
                </tr>
                {grupo.permissoes.map((permissao, i) => (
                  <tr
                    key={permissao.chave}
                    className={i === grupo.permissoes.length - 1 ? '' : 'border-b border-divisoria'}
                  >
                    <td className="py-2.5 pr-3 pl-5 text-[13px] text-tinta">
                      {permissao.rotulo}
                      {niveis[permissao.chave] === 'none' && (
                        <p className="text-[12px] text-texto-fraco italic">
                          sem acesso — o módulo some do menu
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <select
                        aria-label={`Nível de "${permissao.rotulo}"`}
                        value={niveis[permissao.chave] ?? 'none'}
                        onChange={(evento) =>
                          setNiveis((atual) => ({ ...atual, [permissao.chave]: evento.target.value as NivelPermissao }))
                        }
                        className="campo inline-block w-auto"
                      >
                        {NIVEIS_SELECIONAVEIS.map((nivel) => (
                          <option key={nivel} value={nivel}>
                            {ROTULO_NIVEL[nivel]}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {erro && (
        <p role="alert" className="mx-5 mt-3 flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] font-medium text-erro-texto">
          <Icone nome="error" tamanho={16} className="mt-px shrink-0" />
          <span>{erro}</span>
        </p>
      )}

      <footer className="sticky bottom-0 flex min-h-[64px] shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-borda bg-superficie-suave px-5 py-3">
        <Botao type="submit" disabled={salvando}>
          {salvando ? 'Salvando…' : papel ? 'Salvar alterações' : 'Criar papel'}
        </Botao>
      </footer>
    </form>
  )
}
